import { Icon, ProgressBar } from '@perch-labs/shrike';
import {
  DeleteButton,
  DocumentContainer,
  PreviewContainer,
} from '../document-uploader.style';
import { Container } from '../../layout/container.style';
import { Description } from '../../layout/typography.style';

export interface DocumentPreviewProps {
  onDelete: () => void;
  errorCode?: string;
  base64Image?: string;
  uploaded: boolean;
  fileType?: string;
}

export const DocumentPreview = ({
  errorCode,
  onDelete,
  base64Image,
  uploaded,
  fileType,
}: DocumentPreviewProps) => {
  const status = (uploaded && 'success') || (errorCode && 'error') || 'pending';
  const error =
    errorCode && (ERROR_CODES[errorCode] || ERROR_CODES['default-code']);

  const isPdf = fileType === 'application/pdf';

  return (
    <PreviewContainer testId="document-preview" mt={3}>
      <DocumentContainer backgroundImage={base64Image ?? ''} mr={4}>
        {isPdf && (
          <Icon icon="DocumentInverted" title="pdf-document" scale={4} />
        )}
      </DocumentContainer>
      {status === 'pending' && (
        <Container display="flex" flexDirection="column">
          <b>Uploading...</b>
          <ProgressBar maxValue={5000} automatic />
        </Container>
      )}
      {status === 'success' && (
        <Container display="flex" alignItems="center">
          <Icon
            icon="CheckmarkCircleInverted"
            color="accent4"
            margin={{ right: '8px' }}
          />
          <b>Upload successful</b>
        </Container>
      )}
      {status === 'error' && (
        <Container>
          <Description color="error" fontWeight="bold" lineHeight="150%" mb={0}>
            {error?.reason}
          </Description>
          <Description mb={0}>{error?.description}</Description>
        </Container>
      )}
      {status !== 'pending' && (
        <DeleteButton
          onClick={() => onDelete()}
          data-testid="delete-upload-button"
        >
          <Icon icon="UIDelete" title="delete-document" />
        </DeleteButton>
      )}
    </PreviewContainer>
  );
};

export const ERROR_CODES = {
  'already-uploaded': {
    reason: 'Already uploaded',
    description: 'Upload a different file',
  },
  'file-invalid-type': {
    reason: 'Unsupported format',
    description: 'Try a JPG, PNG, HEIC, or PDF',
  },
  'file-too-large': {
    reason: 'File is too large',
    description: 'Must be ≤ 5MB',
  },
  'max-files-exceeded': {
    reason: 'Upload failed',
    description: 'Exceeded max number of files',
  },
  'save-failed': {
    reason: 'Failed to save',
    description: 'Try uploading again',
  },
  'too-many-files': {
    reason: 'Upload failed',
    description: 'Exceeded max number of files',
  },
  'default-code': {
    reason: 'Upload failed',
    description: 'Try a different document',
  },
};

export type UploadErrorCode = keyof typeof ERROR_CODES;
